import API from 'shared/api/API';
import { GET, POST, PATCH, DELETE } from 'shared/utils/Executer';
import { STAFF_SERVICE_URL } from 'shared/constants/APIconstants';

class StaffAPI {
  /**
   * List the users in the configured staff org.
   * @returns {Promise<{ data: { users: Array }, error?: any }>}
   */
  async list() {
    return await API.client.execute(GET, STAFF_SERVICE_URL);
  }

  /**
   * List a staff user's subscriptions across orgs (active + hidden).
   * @param {Object} params
   * @param {string} params.userId
   * @returns {Promise<{ data: { subscriptions: Array }, error?: any }>}
   */
  async listSubscriptions({ userId }) {
    return await API.client.execute(GET, `${STAFF_SERVICE_URL}${userId}/subscriptions`);
  }

  /**
   * Create a new subscription for a staff user in another org. Lands as
   * `isHidden: true` by default — the new sub won't appear in customer-org
   * member lists or admin lookups.
   * @param {Object} params
   * @param {string} params.userId
   * @param {string} params.organizationId
   * @param {string} params.permissions
   * @param {string} [params.userEmail] — defaults to the staff user's primary email server-side.
   * @returns {Promise<{ data: { subscription: Object }, error?: any }>}
   */
  async createSubscription({ userId, organizationId, permissions, userEmail }) {
    const body = { organizationId, permissions };
    if (userEmail) body.userEmail = userEmail;
    return await API.client.execute(POST, `${STAFF_SERVICE_URL}${userId}/subscriptions`, body);
  }

  /**
   * Update a staff user's subscription permissions. When dropping from
   * COLLABORATOR+ to a non-COLLABORATOR level, `altOwnerId` is required so
   * ownership of content in that org is transferred before the perms drop.
   * @param {Object} params
   * @param {string} params.userId
   * @param {string} params.subscriptionId
   * @param {string} params.permissions
   * @param {string} [params.altOwnerId]
   * @returns {Promise<{ data: { subscription: Object }, error?: any }>}
   */
  async updateSubscription({ userId, subscriptionId, permissions, altOwnerId }) {
    const body = { permissions };
    if (altOwnerId) body.altOwnerId = altOwnerId;
    return await API.client.execute(
      PATCH,
      `${STAFF_SERVICE_URL}${userId}/subscriptions/${subscriptionId}`,
      body,
    );
  }

  /**
   * Soft-delete a staff user's subscription. `altOwnerId` is required when the
   * subscription is COLLABORATOR+; ownership of the user's content in that org
   * transfers before the sub is removed.
   * @param {Object} params
   * @param {string} params.userId
   * @param {string} params.subscriptionId
   * @param {string} [params.altOwnerId]
   */
  async deleteSubscription({ userId, subscriptionId, altOwnerId }) {
    // Backend reads altOwnerId from query string (matching the deleteUser convention).
    const config = altOwnerId ? { params: { altOwnerId } } : undefined;
    return await API.client.execute(
      DELETE,
      `${STAFF_SERVICE_URL}${userId}/subscriptions/${subscriptionId}`,
      config,
    );
  }

  /**
   * List the users eligible to inherit ownership in the subscription's org —
   * those with COLLABORATOR+ in that org, excluding the staff user themself.
   * Used to populate the alt-owner dropdown on downgrade/delete.
   * @param {Object} params
   * @param {string} params.userId
   * @param {string} params.subscriptionId
   * @returns {Promise<{ data: { owners: Array<{ id: string, email: string, firstName: string, lastName: string, permissions: string }> }, error?: any }>}
   */
  async getSubscriptionEligibleOwners({ userId, subscriptionId }) {
    return await API.client.execute(
      GET,
      `${STAFF_SERVICE_URL}${userId}/subscriptions/${subscriptionId}/eligible-owners`,
    );
  }
}

export default new StaffAPI();
